<template>
    <page-layout :body-style="{ paddingTop: 0 }">
        <template #header>
            <g-button icon="angle-left" theme="live" size="small" @click="handleGoBack"> Back </g-button>
        </template>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <div :key="productImageKey" class="product-item-image">
                <g-image-thumbnail
                    v-if="variantImage"
                    :image-link="variantImage"
                    image-description="product variant image"
                    image-units-string="%"
                    background-color="$g-color-grey-1"
                    :background-blur="false"
                    display-border
                />
                <g-carousel v-else-if="productImages.length > 1" :img-urls="productImages" display-border lazy-load />
                <g-image-thumbnail
                    v-else
                    :image-link="productImage"
                    image-description="product image"
                    image-units-string="%"
                    background-color="$g-color-grey-1"
                    :background-blur="false"
                    display-border
                />
            </div>
            <g-typography tag="h2" font-weight="semibold" margins>
                {{ productName }}
            </g-typography>
            <g-typography v-if="showPrice" tag="h3" margins>
                {{ productPrice }}
            </g-typography>

            <div class="options-container">
                <div v-for="(option, index) in options" :key="option.id" class="select-input">
                    <g-select
                        v-model="optionValues[index]"
                        :placeholder="option.name"
                        style="width: 100%"
                        size="large"
                        :disabled="readOnly"
                    >
                        <g-select-option v-for="value in option.values" :key="value.id" :value="value.id">
                            <div v-html="$sanitize(value.name)" />
                        </g-select-option>
                    </g-select>
                </div>
                <g-typography v-if="!inStock" tag="p" color="error"> Out of stock </g-typography>
            </div>
            <g-typography tag="h4" v-html="$sanitize(productDescription)" />
        </template>
        <template #footer>
            <g-button
                size="xlarge"
                type="primary"
                shape="round"
                theme="live"
                :disabled="submitDisabled"
                block
                @click="handleAddProduct"
            >
                Add Product
            </g-button>
        </template>
    </page-layout>
</template>

<script>
    import get from 'lodash/get';
    import PageLayout from '../../layouts/PageLayout.vue';
    import { PLACEHOLDER_IMAGE } from '../../../constants/cdn-assets';
    import { formatCurrency } from '../../../helpers/formatters';
    import { generateUUID } from 'ant-design-vue/lib/vc-select/util';
    import ProductImageMixin from './mixins/ProductImageMixin';

    export default {
        name: 'ProductView',
        mixins: [ProductImageMixin],
        components: { PageLayout },
        props: {
            product: {
                type: Object,
                default: undefined
            },
            campaign: {
                type: Object,
                default: undefined
            },
            isLoading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                optionValues: []
            };
        },
        computed: {
            productId() {
                return get(this.product, 'id', '');
            },
            productImageKey() {
                return this.productId ? this.productId : generateUUID();
            },
            productName() {
                return get(this.product, 'name', '');
            },
            showPrice() {
                return get(this.campaign, 'compensation_settings.show_prices', true);
            },
            currency() {
                return get(this.campaign, 'brand.currency', '');
            },
            productPrice() {
                let price;

                if (get(this.currentVariant, 'price')) {
                    price = this.currentVariant.price;
                } else {
                    price = get(this.product, 'price', 0);
                }

                return formatCurrency(price, this.currency);
            },
            productDescription() {
                return get(this.product, 'requirements', '');
            },
            productImage() {
                return this.product.image || PLACEHOLDER_IMAGE;
            },
            productImages() {
                return this.getImageUrls(this.product);
            },
            options() {
                return get(this.product, 'variants[0].options', []);
            },
            variants() {
                return get(this.product, 'variants', []);
            },
            variantParamId() {
                return this.$route.query.variantId;
            },
            productIndex() {
                return this.$route.query.productIndex;
            },
            readOnly() {
                return !!this.$route.query.readonly;
            },
            optionValuesPayload() {
                return this.optionValues.map((valueId, index) => {
                    return this.options[index].values.find((value) => value.id === valueId);
                });
            },
            variantMap() {
                return this.variants.reduce((result, variant) => {
                    const option_ids = variant.option_values.map((option) => option.id);
                    result[option_ids.sort().join('-')] = variant.id;
                    return result;
                }, {});
            },
            currentVariant() {
                const copyOptionValues = [...this.optionValues];
                const selectedOptionsKey = copyOptionValues.sort().join('-');

                const variantId = this.variantMap[selectedOptionsKey];

                if (variantId) {
                    return this.variants.find((variant) => variant.id === variantId);
                }

                return null;
            },
            variantImage() {
                if (this.productImages.length === 0 || this.currentVariant === null) {
                    return undefined;
                }

                const img = this.productImages.find((item) => {
                    return item.variant_ids.includes(this.currentVariant.external_id);
                });

                return img?.url;
            },
            inStock() {
                if (this.currentVariant) {
                    return !!this.currentVariant.is_in_stock;
                }

                if (
                    this.currentVariant === null &&
                    this.options.length > 0 &&
                    this.optionValues.length > 0 &&
                    this.optionValues.length === this.options.length &&
                    !this.optionValues.includes(undefined)
                ) {
                    return false;
                }

                return !!this.product.is_in_stock;
            },
            submitDisabled() {
                if (this.variantParamId) {
                    return false;
                }

                const optionsMissing = this.options.some((option, index) => {
                    return !this.optionValues[index];
                });

                return !!(optionsMissing || !this.inStock);
            },
            payload() {
                const product = { ...this.product };
                product.variant = this.currentVariant
                    ? this.currentVariant
                    : {
                          option_values: this.optionValuesPayload,
                          options: this.options
                      };
                return product;
            }
        },
        watch: {
            product() {
                this.seedValues();
            }
        },
        methods: {
            handleAddProduct() {
                return this.$emit('add-product', this.payload, this.productIndex);
            },
            seedValues() {
                if (this.variantParamId) {
                    const variant = this.variants.find((variant) => variant.id == this.variantParamId);

                    if (variant) {
                        this.optionValues = variant.option_values.map((option) => {
                            return option.id;
                        });
                    }
                }
            },
            handleGoBack() {
                this.$router.go(-1);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .product-item-image {
        height: 432px;
        margin-bottom: $g-spacing-5;
        text-align: center;
        & img {
            border-radius: $g-border-radius;
        }
    }

    .select-input {
        margin-bottom: $g-spacing-3;
    }

    .options-container {
        margin-bottom: $g-spacing-5;
    }
</style>
